import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Iconmoon, { IconWrapper } from '@deprecated-ui/common/Iconmoon';
import color from '@deprecated-ui/_style/color';
import type { ColumnsType } from 'antd/es/table/interface';
import number from 'utils/number';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import { selectors } from 'features/catalog/slice';
import { useEffect, useMemo, useState } from 'react';
import ModalCompanyWatchlist from 'global/Moderation/Modals/ModalCompanyWatchlist';
import produce from 'immer';
import If from 'global/ErrorHandlers/If';
import { selectors as mainLayoutSelectors } from 'global/MainLayout/slice';
import { convertToSlug } from 'utils/general';
import { useLivePriceSender } from 'global/Datafeed/UseLivePrice/useLivePriceSender';
import { LivePriceSymbolsKeyEnum } from 'global/Datafeed/UseLivePrice/liveprice.store.types';
import numeral from 'numeral';
import Last from './Last';
import Change from './Change';
import ChangePercentage from './ChangePercentage';
import { Th, Td, Table, ButtonFollow, LoadingSpinner } from './styled';

const COLUMN_KEY = {
  SYMBOL: 'symbol',
  COMPANY: 'company',
  PRICE: 'price',
  CHANGE: 'change',
  PERCENT: 'percent',
  VOLUME: 'volume',
  AVG_VOLUME: 'avgvolume',
  ACTION: 'action',
  YIELD: 'yield',
  BID_PRICE: 'bid_price',
  OFFER_PRICE: 'offer_price',
  PERFORMANCE: 'performance',
};

interface TableSectorProps {
  sector: string;
  subsector?: string;
  // liveData?: any;
  symbolList: any;
  isFRSector: boolean;
}

function TableSector({
  sector,
  subsector,
  // liveData,
  symbolList,
  isFRSector,
}: TableSectorProps) {
  const { list, subsectors, isLoading } = useSelector(selectors);
  const { isSideWidgetOpen } = useSelector(mainLayoutSelectors);

  const [tableData, setTableData] = useState<Array<any>>([]);
  // need to manually set row keys for expanding all row
  // since it still not supported by the API
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Modal state
  const [visible, setVisible] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>();

  const SECTOR_INDEKS = 'indeks';

  useLivePriceSender(
    LivePriceSymbolsKeyEnum.SECTOR,
    symbolList,
  );

  useEffect(() => {
    if (sector && subsector && list?.[sector]?.[subsector]) {
      setTableData(list[sector][subsector]);
    }

    if (sector && !subsector && list?.[sector]) {
      const subSectorList = Object.keys(list[sector]);

      const mappedData = subSectorList.map((item) => ({
        subsector_slug: item,
        subsector: subsectors.find((sub) => sub.alias1 === item),
        children: list[sector][item],
      }));

      if (convertToSlug(sector) !== SECTOR_INDEKS) {
        setExpandedRowKeys(subSectorList);
      }

      setTableData(mappedData);
    }

    return () => {
      setTableData([]);
      setExpandedRowKeys([]);
    };
  }, [list, sector, subsector]);

  const handleShowModal = (cId) => {
    setVisible(true);
    setCompanyId(Number(cId));
  };

  const handleCancelModal = () => {
    setVisible(false);
  };

  const handleSuccessFollow = (following: boolean) => {
    let newTableData = [...tableData];
    const FOLLOWING = following ? 1 : 0;
    if (sector && subsector) {
      const updatedTable = produce(newTableData, (draft) => {
        const dataIdx = newTableData.findIndex(
          (item) => Number(item.company_id) === companyId,
        );

        if (dataIdx !== -1) {
          const companyData = newTableData[dataIdx];
          draft[dataIdx].followed = FOLLOWING;
        }
      });

      newTableData = updatedTable;
    }

    if (sector && !subsector) {
      const updatedTable = produce(newTableData, (draft) => {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < newTableData.length; i++) {
          const subSectorChildren = newTableData[i].children;
          const dataIdx = subSectorChildren.findIndex(
            (sub) => Number(sub.company_id) === companyId,
          );

          if (dataIdx !== -1) {
            const companyData = subSectorChildren[dataIdx];
            draft[i].children[dataIdx].followed = FOLLOWING;
            // Remove break to update following status for all company in table
          }
        }
      });

      newTableData = updatedTable;
    }

    setTableData(newTableData);
  };

  const generateId = () => {
    // @ts-ignore
    const crypto = window?.crypto || window?.msCrypto;
    const array = new Uint32Array(1);
    crypto.getRandomValues(array); // Compliant for security-sensitive use cases
    const id = array[0];
    return id;
  };

  const generateRowKey = (company) => {
    // return key for subsector row
    if (company.subsector_slug) {
      return convertToSlug(company.subsector_slug);
    }

    const randomId = generateId();

    return `company-${company.company_id}-${randomId}`;
  };

  const columnsFR: ColumnsType<any> = [
    {
      title: (
        <Th align="right" id="catalog-table-column-yield">
          Yield
        </Th>
      ),
      key: COLUMN_KEY.YIELD,
      dataIndex: 'extra_attributes.fr_attributes.yield',
      render: (_, record) => {
        if (record.subsector_slug) {
          return null;
        }
        return (
          <Td align="right" p="0">
            {numeral(record.extra_attributes.fr_attributes?.yield).format('0.00')}%
          </Td>
        );
      },
      className: 'catalog-table-cell-yield',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-bid-price">
          Harga Beli
        </Th>
      ),
      key: COLUMN_KEY.BID_PRICE,
      render: (_, row) => {
        if (row.subsector_slug) {
          return null;
        }
        return (
          <Td align="right" p="0">
            {numeral(row.extra_attributes.fr_attributes?.bid_price).format('0.00')}%
          </Td>
        );
      },
      className: 'catalog-table-cell-bid-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-offer-price">
          Harga Jual
        </Th>
      ),
      key: COLUMN_KEY.OFFER_PRICE,
      dataIndex: 'extra_attributes.fr_attributes.offer_price',
      shouldCellUpdate: () => false,
      render: (_, record) => {
        if (record.subsector_slug) {
          return null;
        }
        return (
          <Td align="right" p="0">
            {numeral(record.extra_attributes.fr_attributes?.offer_price).format('0.00')}%
          </Td>
        );
      },
      className: 'hide-on-mobile catalog-table-cell-offer-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-perfromance">
          Performa 1Y
        </Th>
      ),
      key: COLUMN_KEY.PERFORMANCE,
      dataIndex: 'extra_attributes.fr_attributes.performance',
      shouldCellUpdate: () => false,
      render: (_, record) => {
        if (record.subsector_slug) {
          return null;
        }

        let textColor = color.midgray;
        let prefix = '';
        const numberPercentage = parseFloat(
          record.extra_attributes.fr_attributes?.performance,
        );

        if (numberPercentage > 0) {
          textColor = color.primary;
          prefix = '+';
        }
        if (numberPercentage < 0) {
          textColor = color.sunsetOrange;
        }

        const value = numeral(record.extra_attributes.fr_attributes?.performance)
          .format('0.00');

        return (
          <Td align="right" color={textColor} p="0">
            {prefix}{value}%
          </Td>
        );
      },
      className: 'hide-on-mobile catalog-table-cell-performance',
    },
  ];

  const columnsDefault: ColumnsType<any> = [
    {
      title: (
        <Th align="right" id="catalog-table-column-price">
          Price
        </Th>
      ),
      key: COLUMN_KEY.PRICE,
      dataIndex: 'last',
      render: (price: string, record) => {
        if (record.subsector_slug) {
          return null;
        }

        return (
          <Last key="last" lastPrice={record.last} symbol={record.symbol} />
        );
      },
      className: 'catalog-table-cell-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-change">
          Change
        </Th>
      ),
      key: COLUMN_KEY.CHANGE,
      dataIndex: 'change',
      render: (text, row) => {
        if (row.subsector_slug) {
          return null;
        }

        return <Change key="change" change={row.change} symbol={row.symbol} />;
      },
      className: 'catalog-table-cell-change',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-change">
          %
        </Th>
      ),
      key: COLUMN_KEY.PERCENT,
      dataIndex: 'percent',
      render: (text, row) => {
        if (row.subsector_slug) {
          return null;
        }
        return (
          <ChangePercentage
            key="percent"
            change={row.change}
            percent={row.percent}
            symbol={row.symbol}
          />
        );
      },
      className: 'catalog-table-cell-change',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-marketcap">
          Volume
        </Th>
      ),
      key: COLUMN_KEY.VOLUME,
      dataIndex: 'volume',
      shouldCellUpdate: () => false,
      render: (vol: string, row: any) => {
        const defaultValue =
          vol === 'NA' || vol === ''
            ? 'NA'
            : number.parseCountAbbrv4({
              value: vol,
              decPlaces: 2,
              abbreviation: 2,
            });

        if (row.subsector_slug) {
          return null;
        }

        return (
          <Td align="right" p="0">
            {defaultValue}
          </Td>
        );
      },
      className: 'hide-on-mobile catalog-table-cell-marketcap',
    },
    {
      title: (
        <Th
          align="right"
          id="catalog-table-column-liquidity"
          p="15px 0px 15px 20px"
        >
          Avg Volume
        </Th>
      ),
      key: COLUMN_KEY.AVG_VOLUME,
      dataIndex: 'avgvolume',
      shouldCellUpdate: () => false,
      render: (avg: string, row: any) => {
        const defaultValue =
          avg === 'NA' || avg === ''
            ? 'NA'
            : number.parseCountAbbrv4({
              value: parseInt(avg, 10),
              decPlaces: 2,
              abbreviation: 2,
            });

        if (row.subsector_slug) {
          return null;
        }

        return (
          <Td align="right" p="0">
            {defaultValue}
          </Td>
        );
      },
      className: 'hide-on-mobile catalog-table-cell-liquidity',
    },
  ];

  const columns = useMemo(() => {
    let data: ColumnsType<any> = [
      {
        title: <Th id="catalog-table-column-symbol">Symbol</Th>,
        dataIndex: 'symbol',
        key: COLUMN_KEY.SYMBOL,
        shouldCellUpdate: () => true,
        render: (symbol: string, row: any) => {
          let children = (
            // @ts-expect-error
            <Td bold ellipsis>
              <Link href={`/symbol/${symbol}`}>
                <a>{symbol}</a>
              </Link>
            </Td>
          );

          if (!row.company_id && row.subsector_slug) {
            const rowKey = generateRowKey(row);
            const icon = expandedRowKeys.includes(rowKey) ? 'minus' : 'plus';
            children = (
              <Td color={color.primary} style={{ cursor: 'pointer' }}>
                <Flex justify="space-between" align="center">
                  <Link href={`/sector/${sector}/${row.subsector_slug}`}>
                    <a>{row.subsector?.name}</a>
                  </Link>
                  <IconWrapper>
                    <Iconmoon className={`icon-toolbar_${icon}`} />
                  </IconWrapper>
                </Flex>
              </Td>
            );
          }
          return {
            children,
            props: {
              colSpan: row.subsector_slug ? 8 : 1,
            },
          };
        },
        className: 'catalog-table-cell-symbol',
      },
      {
        title: <Th id="catalog-table-column-company">Company</Th>,
        key: COLUMN_KEY.COMPANY,
        dataIndex: 'name',
        shouldCellUpdate: () => false,
        render: (company: string, row: any) => {
          if (row.subsector_slug) {
            return null;
          }
          return (
            <Td>
              <Text height="44px" style={{ width: '260px' }} ellipsis>
                {company}
              </Text>
            </Td>
          );
        },
        className: `hide-on-tablet catalog-table-cell-company ${
          isSideWidgetOpen && 'hide-cell'
        }`,
      },
    ];

    const valueData = isFRSector
      ? columnsFR
      : columnsDefault;

    data = [
      ...data,
      ...valueData,
      {
        title: <Th>&nbsp;</Th>,
        key: COLUMN_KEY.ACTION,
        dataIndex: 'followed',
        shouldCellUpdate: () => true,
        render: (following: string, row: any) => {
          if (row.subsector_slug) {
            return null;
          }

          return (
            <Td align="right">
              <ButtonFollow
                className={!!following && 'following'}
                onClick={() => handleShowModal(row.company_id)}
              >
                {following ? 'Following' : 'Follow'}
              </ButtonFollow>
            </Td>
          );
        },
        className: 'hide-on-mobile catalog-table-cell-liquidity',
      },
    ];
    return data;
  }, [isFRSector]);

  return (
    <>
      <If condition={isLoading}>
        <LoadingSpinner />
      </If>
      {/*
        This 'duplicated' component was added because if we combine the condition,
        the tree table data will get an error displaying row title. Not really sure why
        this is happening.
      */}
      <If condition={!isLoading && tableData.length === 0}>
        <LoadingSpinner />
      </If>
      <Table
        isLoading={isLoading || tableData.length === 0}
        dataSource={tableData}
        columns={columns}
        pagination={false}
        rowKey={(record) => generateRowKey(record)}
        expandable={{
          expandedRowKeys,
          expandRowByClick: true,
          expandIcon: () => null,
          indentSize: 0,
          onExpand: (expanded, record:any) => {
            const slugify = generateRowKey(record);
            if (record.subsector_slug) {
              const filtering = expandedRowKeys.filter(
                (item) => item !== slugify,
              );

              if (expanded) {
                setExpandedRowKeys((prevState) => [...prevState, slugify]);
              } else {
                setExpandedRowKeys(filtering);
              }
            }
          },
        }}
      />
      <ModalCompanyWatchlist
        visible={visible}
        onCancel={handleCancelModal}
        companyId={companyId}
        onFollow={handleSuccessFollow}
      />
    </>
  );
}

export default TableSector;
